/**
 * @file DeliveryTariffCard.tsx
 * @description Карточка одного тарифа в результатах расчёта (phase19.12 шаг 5).
 *
 * Кроме цены и срока показывает главное для менеджера: что увидит клиент и
 * насколько это расходится с ценой, которую он видел на витрине.
 */

import { Check } from "lucide-react";

import { Badge } from "../ui/badge";
import type { DeliveryQuoteOption } from "../../lib/api-shop-carriers";
import { formatPeriod, formatRub } from "../../lib/delivery-quote";

interface Props {
  option: DeliveryQuoteOption;
  selected: boolean;
  onSelect: () => void;
  /** Цена и тариф, которые клиент видел на витрине. */
  clientTariffCode?: number | null;
  clientPrice?: number | null;
  disabled?: boolean;
}

export function DeliveryTariffCard({
  option,
  selected,
  onSelect,
  clientTariffCode,
  clientPrice,
  disabled,
}: Props) {
  const sameTariff = clientTariffCode !== null && clientTariffCode === option.tariffCode;
  const delta =
    sameTariff && clientPrice !== null && clientPrice !== undefined
      ? Math.round((option.price - clientPrice) * 100) / 100
      : null;
  const markup = Math.round((option.price - option.carrierPrice) * 100) / 100;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={`flex min-h-11 w-full flex-col gap-1 rounded-lg p-3 text-left transition-colors ${
        selected
          ? "bg-primary/8 [box-shadow:inset_0_0_0_2px_var(--primary)]"
          : "[box-shadow:inset_0_0_0_1px_var(--outline-variant)] hover:bg-surface-low"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium">
            <span className="tabular-nums text-on-surface-variant">{option.tariffCode}</span>{" "}
            {option.tariffName}
          </div>
          <div className="text-xs text-on-surface-variant">
            {formatPeriod(option.periodMin, option.periodMax)}
            {option.description ? ` · ${option.description}` : ""}
          </div>
        </div>
        {selected ? <Check className="mt-0.5 size-4 shrink-0 text-primary" /> : null}
      </div>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-base font-semibold">
          {option.free ? "Клиенту бесплатно" : `Клиенту ${formatRub(option.price)}`}
        </span>
        <span className="text-xs text-on-surface-variant">
          СДЭК {formatRub(option.carrierPrice)}
          {markup > 0 ? ` (+${formatRub(markup)} наценка)` : ""}
        </span>
      </div>

      <div className="flex flex-wrap gap-1">
        {delta !== null && delta === 0 ? (
          <Badge variant="success">✓ как на витрине</Badge>
        ) : null}
        {delta !== null && delta > 0 ? (
          <Badge variant="warning">
            Дороже, чем видел клиент: {formatRub(clientPrice ?? 0)} → {formatRub(option.price)} (+
            {formatRub(delta)})
          </Badge>
        ) : null}
        {delta !== null && delta < 0 ? (
          <Badge variant="primary">
            Дешевле: {formatRub(clientPrice ?? 0)} → {formatRub(option.price)} (−
            {formatRub(Math.abs(delta))})
          </Badge>
        ) : null}
      </div>
    </button>
  );
}
